import React from 'react';

type DailyUsageLineChartProps = {
  points: {
    date: string;
    totals: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
    records: number;
  }[];
  onPointClick: (date: string) => void;
  onPointHover: (index: number | null) => void;
  hoveredIndex: number | null;
};

export const DailyUsageLineChart: React.FC<DailyUsageLineChartProps> = ({
  points,
  onPointClick,
  onPointHover,
  hoveredIndex,
}) => {
  if (!points.length) return null;

  const totalValues = points.map((p) => p.totals.total_tokens);
  const maxValue = Math.max(1, ...totalValues);
  const avgValue = totalValues.reduce((a, b) => a + b, 0) / totalValues.length || 0;

  const findOptimalScale = (max: number, avg: number) => {
    if (max === 0) return { max: 100, step: 25 };
    const scales = [
      { unit: 100 },
      { unit: 1000 },
      { unit: 10000 },
      { unit: 100000 },
      { unit: 1000000 },
      { unit: 10000000 },
      { unit: 100000000 },
      { unit: 1000000000 },
    ];
    for (const scale of scales) {
      const scaledMax = Math.ceil(max / scale.unit) * scale.unit;
      const scaledAvg = avg / scale.unit;
      const avgRatio = scaledAvg / (scaledMax / scale.unit);
      if (avgRatio >= 0.3 && avgRatio <= 0.7 && scaledMax / scale.unit <= 10) {
        return { max: scaledMax, step: scale.unit / 4 };
      }
    }
    const targetMax = Math.max(max, avg * 2);
    const roundedMax = Math.ceil(targetMax / 1000) * 1000;
    return { max: roundedMax, step: roundedMax / 4 };
  };

  const scale = findOptimalScale(maxValue, avgValue);
  const yMax = scale.max;
  const yStep = yMax / 4;

  const width = Math.max(600, points.length * 40);
  const height = 260;
  const paddingLeft = 60;
  const paddingRight = 24;
  const paddingTop = 20;
  const paddingBottom = 36;
  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;

  const toX = (index: number) =>
    paddingLeft +
    (points.length === 1 ? chartWidth / 2 : (index / (points.length - 1)) * chartWidth);
  const toY = (value: number) => paddingTop + chartHeight - (value / yMax) * chartHeight;

  const handlePointClick = (event: React.MouseEvent<SVGCircleElement>, date: string) => {
    event.stopPropagation();
    onPointClick(date);
  };

  return (
    <div className="relative w-full overflow-hidden" style={{ height: `${height}px` }}>
      <div
        className="absolute left-0 top-0 bottom-0 z-20 pointer-events-none bg-white"
        style={{ width: `${paddingLeft}px` }}
      >
        {Array.from({ length: 5 }).map((_, index) => {
          const value = yStep * index;
          const y = toY(value);
          return (
            <div
              key={index}
              className="absolute text-right pr-2"
              style={{ top: `${y - 6}px`, width: `${paddingLeft}px` }}
            >
              <span className="text-[10px] text-neutral-400">
                {Math.round(value).toLocaleString()}
              </span>
            </div>
          );
        })}
      </div>
      <div
        className="overflow-x-auto scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-track-transparent"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#d1d5db transparent',
          marginLeft: `${paddingLeft}px`,
          height: `${height}px`,
        }}
      >
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full"
          style={{ minWidth: `${width}px`, height: `${height}px` }}
          role="img"
          aria-label="Daily LLM token usage"
        >
          {Array.from({ length: 5 }).map((_, index) => {
            const value = yStep * index;
            const y = toY(value);
            return (
              <g key={index}>
                <line
                  x1={paddingLeft}
                  y1={y}
                  x2={paddingLeft + chartWidth}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeWidth={0.5}
                  strokeDasharray="2,2"
                />
              </g>
            );
          })}
          {points.map((point, index) => {
            const x = toX(index);
            return (
              <g key={point.date}>
                <text x={x} y={height - 8} fontSize={10} fill="#6b7280" textAnchor="middle">
                  {point.date.slice(5)}
                </text>
              </g>
            );
          })}
          <g fill="none" stroke="#b91c1c" strokeWidth={2}>
            <path
              d={
                totalValues
                  .map((value, index) => {
                    const x = toX(index);
                    const y = toY(value);
                    return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
                  })
                  .join(' ') || ''
              }
            />
          </g>
          {totalValues.map((value, index) => {
            const x = toX(index);
            const y = toY(value);
            const pointId = `total-${index}`;
            return (
              <g key={pointId}>
                <circle
                  cx={x}
                  cy={y}
                  r={4}
                  fill="#ffffff"
                  stroke="#b91c1c"
                  strokeWidth={2}
                  data-point-id={pointId}
                  className="cursor-pointer hover:r-5 transition-all"
                  onClick={(event) => handlePointClick(event, points[index].date)}
                  onMouseEnter={() => onPointHover(index)}
                  onMouseLeave={() => onPointHover(null)}
                />
                {hoveredIndex === index && (
                  <g>
                    <rect x={x - 35} y={y - 22} width={70} height={18} fill="#1f2937" rx={4} />
                    <text
                      x={x}
                      y={y - 8}
                      textAnchor="middle"
                      fontSize={10}
                      fill="#ffffff"
                      fontWeight="500"
                    >
                      {value.toLocaleString()}
                    </text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

type DailyUsageScatterChartProps = {
  day: string;
  data:
    | {
        date: string;
        events: {
          timestamp: string;
          prompt_tokens: number;
          completion_tokens: number;
          total_tokens: number;
          model?: string | null;
          source?: string | null;
          tool_names: string[];
        }[];
      }
    | null
    | undefined;
  isLoading: boolean;
  onPointHover: (index: number | null) => void;
  hoveredIndex: number | null;
};

export const DailyUsageScatterChart: React.FC<DailyUsageScatterChartProps> = ({
  day,
  data,
  isLoading,
  onPointHover,
  hoveredIndex,
}) => {
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-neutral-500">
        Loading daily usage…
      </div>
    );
  }

  const events = data?.events ?? [];
  if (!events.length) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-neutral-500">
        No LLM calls recorded on {day}.
      </div>
    );
  }

  const totalValues = events.map((e) => e.total_tokens);
  const maxValue = Math.max(1, ...totalValues);
  const avgValue = totalValues.reduce((a, b) => a + b, 0) / totalValues.length || 0;

  const findOptimalScale = (max: number, avg: number) => {
    if (max === 0) return { max: 100, step: 25 };
    const scales = [
      { unit: 100 },
      { unit: 1000 },
      { unit: 10000 },
      { unit: 100000 },
      { unit: 1000000 },
      { unit: 10000000 },
      { unit: 100000000 },
      { unit: 1000000000 },
    ];
    for (const scale of scales) {
      const scaledMax = Math.ceil(max / scale.unit) * scale.unit;
      const scaledAvg = avg / scale.unit;
      const avgRatio = scaledAvg / (scaledMax / scale.unit);
      if (avgRatio >= 0.3 && avgRatio <= 0.7 && scaledMax / scale.unit <= 10) {
        return { max: scaledMax, step: scale.unit / 4 };
      }
    }
    const targetMax = Math.max(max, avg * 2);
    const roundedMax = Math.ceil(targetMax / 1000) * 1000;
    return { max: roundedMax, step: roundedMax / 4 };
  };

  const scale = findOptimalScale(maxValue, avgValue);
  const yMax = scale.max;
  const yStep = yMax / 4;

  const parseTime = (timestamp: string) => {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return 0;
    return date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600;
  };

  const hours = events.map((e) => parseTime(e.timestamp));
  const minHour = Math.min(...hours, 0);
  const maxHour = Math.max(...hours, 23.999);

  const width = Math.max(900, events.length * 40);
  const height = 260;
  const paddingLeft = 60;
  const paddingRight = 24;
  const paddingTop = 20;
  const paddingBottom = 40;
  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;

  const toX = (hour: number) =>
    paddingLeft +
    (maxHour === minHour ? chartWidth / 2 : ((hour - minHour) / (maxHour - minHour)) * chartWidth);
  const toY = (value: number) => paddingTop + chartHeight - (value / yMax) * chartHeight;

  return (
    <div className="relative w-full overflow-hidden" style={{ height: `${height}px` }}>
      <div
        className="absolute left-0 top-0 bottom-0 z-20 pointer-events-none bg-white"
        style={{ width: `${paddingLeft}px` }}
      >
        {Array.from({ length: 5 }).map((_, index) => {
          const value = yStep * index;
          const y = toY(value);
          return (
            <div
              key={index}
              className="absolute text-right pr-2"
              style={{ top: `${y - 6}px`, width: `${paddingLeft}px` }}
            >
              <span className="text-[10px] text-neutral-400">
                {Math.round(value).toLocaleString()}
              </span>
            </div>
          );
        })}
      </div>
      <div
        className="overflow-x-auto scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-track-transparent"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#d1d5db transparent',
          marginLeft: `${paddingLeft}px`,
          height: `${height}px`,
        }}
      >
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full"
          style={{ minWidth: `${width}px`, height: `${height}px` }}
          role="img"
          aria-label={`LLM token usage for ${day}`}
        >
          {Array.from({ length: 5 }).map((_, index) => {
            const value = yStep * index;
            const y = toY(value);
            return (
              <g key={index}>
                <line
                  x1={paddingLeft}
                  y1={y}
                  x2={paddingLeft + chartWidth}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeWidth={0.5}
                  strokeDasharray="2,2"
                />
              </g>
            );
          })}
          {Array.from({ length: 6 }).map((_, index) => {
            const hour = (24 / 6) * index;
            const x = toX(hour);
            return (
              <g key={index}>
                <line
                  x1={x}
                  y1={paddingTop}
                  x2={x}
                  y2={paddingTop + chartHeight}
                  stroke="#e5e7eb"
                  strokeWidth={0.5}
                />
                <text x={x} y={height - 10} fontSize={10} fill="#6b7280" textAnchor="middle">
                  {`${String(Math.floor(hour)).padStart(2, '0')}:00`}
                </text>
              </g>
            );
          })}
          {events.map((event, index) => {
            const value = event.total_tokens;
            const x = toX(parseTime(event.timestamp));
            const y = toY(value);
            const pointId = `total-${index}`;
            return (
              <g key={pointId}>
                <circle
                  cx={x}
                  cy={y}
                  r={4}
                  fill="#b91c1c"
                  data-point-id={pointId}
                  className="cursor-default hover:r-5 transition-all"
                  onMouseEnter={() => onPointHover(index)}
                  onMouseLeave={() => onPointHover(null)}
                />
                {hoveredIndex === index && (
                  <g>
                    <rect x={x - 50} y={y - 50} width={100} height={42} fill="#1f2937" rx={4} />
                    <text
                      x={x}
                      y={y - 35}
                      textAnchor="middle"
                      fontSize={9}
                      fill="#ffffff"
                      fontWeight="600"
                    >
                      Total: {event.total_tokens.toLocaleString()}
                    </text>
                    <text x={x} y={y - 25} textAnchor="middle" fontSize={9} fill="#a5b4fc">
                      Prompt: {event.prompt_tokens.toLocaleString()}
                    </text>
                    <text x={x} y={y - 15} textAnchor="middle" fontSize={9} fill="#34d399">
                      Completion: {event.completion_tokens.toLocaleString()}
                    </text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};
